import { createApi } from "@reduxjs/toolkit/query/react";

import apiConfig from "configs/apiConfig";
import { REDUCER_PATHS } from "store/config";
import { transformResponseHandler } from "utils/transformResponseHandler";

import { customFetchBase } from ".";

const saleProceedApi = createApi({
  reducerPath: REDUCER_PATHS.SALE_PROCEED,
  baseQuery: customFetchBase,
  endpoints: (builder) => ({
    saleProceedList: builder.query({
      query: (data) => ({
        url: `${apiConfig.SALE_PROCEED}?page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data.searchText}&sortColumn=${data.sortColumn}&sortDirection=${data.sortDirection}`,
        method: "GET",
      }),
      providesTags: ["saleProceedList"],
    }),
    saleProceedUserList: builder.query({
      query: (data) => ({
        url: `${apiConfig.SALE_PROCEED_USER}/?page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data.searchText}&sortColumn=${data.sortColumn}&sortDirection=${data.sortDirection}&saleProceedId=${data?.saleProceedId}`,
        method: "GET",
      }),
      providesTags: ["saleProceedList"],
    }),
    editSaleProceedList: builder.mutation({
      query: (data) => ({
        url: `${apiConfig.SALE_PROCEED}/${data?.Id}`,
        body: data?.body,
        method: "PUT",
      }),
      invalidatesTags: ["saleProceedList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    saleProceedListChangeStatus: builder.mutation({
      query: (data) => ({
        url: `${apiConfig.SALE_PROCEED_UPDATE_STATUS}?saleProceedId=${data?.saleProceedId}&status=${data?.status}`,
        body: data,
        method: "PATCH",
      }),
      invalidatesTags: ["saleProceedList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    saleProceedListDelete: builder.mutation({
      query: (data) => ({
        url: apiConfig.SALE_PROCEED,
        method: "DELETE",
        body: data,
      }),
      invalidatesTags: ["saleProceedList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    addSaleProceedList: builder.mutation({
      query: (data) => ({
        url: apiConfig.SALE_PROCEED,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["saleProceedList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    saleProceedDetails: builder.query({
      query: (data) => ({
        url: `${apiConfig.SALE_PROCEED_DETAILS}?saleProceedId=${data?.saleProceedId}&page=${data?.page}&pageSize=${data?.pageSize}`,
        method: "GET",
      }),
      providesTags: ["saleProceedList"],
    }),
    saleProceedInvestmentList: builder.query({
      query: (data) => ({
        url: `${apiConfig.SALE_PROCEED_INVESTMENT_LIST}?propertyId=${data?.propertyId}&page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data?.searchText}&sortColumn=${data?.sortColumn}&sortDirection=${data?.sortDirection}`,
        method: "GET",
      }),
      providesTags: ["saleProceedList"],
    }),
    transactionReportList: builder.query({
      query: (data) => {
        const fromDate = data?.fromDate ? `&fromDate=${data.fromDate}` : "";
        const toDate = data?.toDate ? `&toDate=${data.toDate}` : "";
        return {
          url: `${apiConfig.TRANSACTION_REPORT}?page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data?.searchText}&sortColumn=${data?.sortColumn}&sortDirection=${data?.sortDirection}${fromDate}${toDate}&filter=${data?.filter}`,
          method: "GET",
        };
      },
      providesTags: ["transactionReportList"],
    }),
    saleProceedNotificationDetails: builder.query({
      query: (data) => ({
        url: `${apiConfig.PROPERTY_SALE_PROCEED_NOTIFICATION_LIST}/${data?.notificationId}`,
        method: "GET",
      }),
      providesTags: ["saleProceedNotificationList"],
    }),
    saleProceedNotificationList: builder.query({
      query: (data) => ({
        url: `${apiConfig.PROPERTY_SALE_PROCEED_NOTIFICATION_LIST}?page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data?.searchText}&sortColumn=${data?.sortColumn}&sortDirection=${data?.sortDirection}`,
        method: "GET",
      }),
      providesTags: ["saleProceedNotificationList"],
    }),
    addSaleProceedNotificationList: builder.mutation({
      query: (data) => ({
        url: apiConfig.ADD_SALE_PROCEED_NOTIFICATION_LIST,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["saleProceedNotificationList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    notificationList: builder.query({
      query: (data) => ({
        url: `${apiConfig.NOTIFICATION_LIST}?page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data?.searchText}&sortColumn=${data?.sortColumn}&sortDirection=${data?.sortDirection}`,
        method: "GET",
      }),
      providesTags: ["NotificationList"],
    }),
    notificationListChangeStatus: builder.mutation({
      query: (data) => ({
        url: apiConfig.NOTIFICATION_LIST_CHANGE_STATUS,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["NotificationList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    saleProceedListDownload: builder.mutation({
      query: (data) => {
        const params = new URLSearchParams();

        if (data.filter) params.append("filter", data.filter);
        if (data.column) params.append("column", data.column);

        return {
          url: `${apiConfig.SALE_PROCEED_LIST_DOWNLOAD}/${
            data.type
          }?${params.toString()}`,
          method: "GET",
        };
      },
      invalidatesTags: ["saleProceedList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    saleProceedNotificationListDownload: builder.mutation({
      query: (data) => {
        const params = new URLSearchParams();

        if (data.filter) params.append("filter", data.filter);
        if (data.column) params.append("column", data.column);

        return {
          url: `${apiConfig.SALE_PROCEED_NOTIFICATION_LIST_DOWNLOAD}/${
            data.type
          }?${params.toString()}`,
          method: "GET",
        };
      },
      invalidatesTags: ["saleProceedNotificationList"],
    }),
  }),
});

export const {
  useAddSaleProceedListMutation,
  useLazySaleProceedListQuery,
  useLazySaleProceedUserListQuery,
  useSaleProceedListChangeStatusMutation,
  useSaleProceedListDeleteMutation,
  useLazySaleProceedDetailsQuery,
  useEditSaleProceedListMutation,
  useLazySaleProceedInvestmentListQuery,
  useLazyTransactionReportListQuery,
  useLazySaleProceedNotificationDetailsQuery,
  useLazySaleProceedNotificationListQuery,
  useAddSaleProceedNotificationListMutation,
  useNotificationListChangeStatusMutation,
  useLazyNotificationListQuery,
  useSaleProceedListDownloadMutation,
  useSaleProceedNotificationListDownloadMutation,
} = saleProceedApi;

export default saleProceedApi;
